/**
 * Platform availability — `/countries/:cc/admin/platform-availability`. ONE
 * country's answer over the group's platform vocabulary (migration 38, owner
 * decision D-2).
 *
 * AUTHORED (no Figma exists for the admin surface); assembled from existing
 * primitives in the `TaxGroupsPage` idiom.
 *
 * ===========================================================================
 * WHAT THIS SCREEN SAYS OUT LOUD, BECAUSE D-2 IS EASY TO MIS-READ
 * ===========================================================================
 * A MISSING `country_platforms` ROW MEANS THE VALUE IS OFFERED (D-2). The cost D-2
 * names — that a picker cannot distinguish "deliberately on here" from "never
 * considered here" — is exactly what the Decision column makes visible: `explicit`
 * is on the wire (`platforms.service.ts:128-140`) so "no decision recorded" renders
 * as its own state instead of masquerading as a choice somebody made.
 *
 * `offered` IS THE PICKER'S ANSWER AND IT COMES FROM THE WIRE (`group_active &&
 * active`, computed server-side), so this screen cannot disagree with what the
 * picker will actually do — a lit switch over a group-retired value would be a lie,
 * and the "Retired group-wide" pill beside it is the truth-teller.
 *
 * WHAT THIS SCREEN CANNOT DO, STRUCTURALLY: rename, re-sort or re-scope the group's
 * values — `PutCountryPlatformsDto` carries `platform_id` + `active` and nothing
 * else, so a Country Admin cannot edit the group's vocabulary from here
 * (`platform.dto.ts:13-21`, pinned by `platforms.e2e-spec.ts` suite (c)). Authoring
 * lives on the group's own screen behind `config.global`.
 *
 * THE GATE is `config.manage` IN THIS COUNTRY — what both routes behind the screen
 * enforce (`platforms.controller.ts:127-147`: `@RequirePerm('config.manage')` +
 * `@CountryScoped()` on GET and PUT). NOT `config.global`: holding the group key is
 * not holding the country one (`platforms.e2e-spec.ts` suite (b), both directions).
 * The `TaxGroupsPage` gate expression, kept exactly.
 *
 * AND THERE IS NO STACKS TWIN — `/countries/:cc/stacks` does not exist (D-2b): a
 * stack is what our engineers build in, not what a country sells.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ToggleLeft } from 'lucide-react';
import { PageHeader } from '../../components/shell/AppShell';
import { Alert, Button, Card, EmptyState, SkeletonRows } from '../../components/ui';
import { useCountryCode } from '../../auth/guards';
import { useSession } from '../../auth/session';
import { COUNTRIES } from '../../domain/countries';
import { ApiRequestError } from '../../lib/api';
import { fetchCountryPlatforms, putCountryPlatforms, vocabularyKeys } from './vocabulary-api';
import type { CountryPlatformRow } from './vocabulary-api';
import { ConfirmDialog } from './ConfirmDialog';
import { NoAdminAccess, errorSentence } from './states';
import { appliesToSentence } from './vocabulary-states';

/** The API's own sentence when it carries one — the NetworkRules pattern. */
function apiSentence(error: unknown, fallback: string): string {
  if (error instanceof ApiRequestError) return error.message;
  return errorSentence(error, fallback);
}

export function PlatformAvailabilityPage(): React.JSX.Element {
  const countryCode = useCountryCode();
  const { can, isGroupAdmin } = useSession();
  const queryClient = useQueryClient();

  const permitted = isGroupAdmin || can(countryCode, 'config.manage');

  const [switchingOff, setSwitchingOff] = useState<CountryPlatformRow | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const flags = useQuery({
    queryKey: vocabularyKeys.countryPlatforms(countryCode),
    queryFn: ({ signal }) => fetchCountryPlatforms(countryCode, signal),
    enabled: permitted,
  });

  const put = useMutation({
    /** One value per save — the UPSERT touches the named row and nothing else. */
    mutationFn: (input: { platform_id: string; active: boolean }) =>
      putCountryPlatforms(countryCode, [input]),
    onSuccess: () => {
      setSwitchingOff(null);
      setActionError(null);
      void queryClient.invalidateQueries({
        queryKey: vocabularyKeys.countryPlatforms(countryCode),
      });
      // The group screen's "Country decisions" column counts rows this PUT creates.
      void queryClient.invalidateQueries({ queryKey: vocabularyKeys.platforms() });
    },
    onError: (error) => {
      setSwitchingOff(null);
      setActionError(apiSentence(error, 'The change was not saved.'));
    },
  });

  if (!permitted) return <NoAdminAccess />;

  const rows = flags.data ?? [];
  const countryName = COUNTRIES[countryCode].name;

  return (
    <div className="flex flex-col gap-4 pb-6">
      <PageHeader
        title="Platform availability"
        subtitle={`Which of the group's platform values ${countryName} offers in its pickers. A value nobody has decided about is offered — switching one off is the deliberate act.`}
      />

      <div className="flex flex-col gap-4 px-6">
        {actionError === null ? null : <Alert tone="danger">{actionError}</Alert>}

        <Card title={`Availability in ${countryName}`}>
          {flags.isPending ? (
            <SkeletonRows rows={3} label="Loading platform availability" />
          ) : flags.isError ? (
            /* NOT an empty vocabulary: "could not be read" is not "nothing is offered". */
            <Alert tone="danger" title="The availability list could not be read">
              {errorSentence(flags.error, 'The server did not answer.')} Nothing below is a
              statement of what this country offers — this is a read failure, not an empty
              register.
            </Alert>
          ) : rows.length === 0 ? (
            <EmptyState
              icon={<ToggleLeft aria-hidden="true" className="size-5" />}
              title="The group has no platform values yet"
              description="There is nothing for this country to offer or withhold. Values are authored group-wide, by a group administrator, on the Platforms screen."
            />
          ) : (
            <table className="w-full border-collapse text-left">
              <thead>
                <tr className="border-b border-line text-th uppercase text-fg-muted">
                  <th scope="col" className="py-2 pr-3 font-normal">Name</th>
                  <th scope="col" className="py-2 pr-3 font-normal">Applies to</th>
                  <th scope="col" className="py-2 pr-3 font-normal">Decision here</th>
                  <th scope="col" className="py-2 pr-3 font-normal">Offered here</th>
                  <th scope="col" className="py-2 text-right font-normal">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <AvailabilityRow
                    key={row.id}
                    row={row}
                    busy={put.isPending}
                    onSwitchOff={() => {
                      setActionError(null);
                      setSwitchingOff(row);
                    }}
                    onSwitchOn={() => {
                      setActionError(null);
                      put.mutate({ platform_id: row.id, active: true });
                    }}
                  />
                ))}
              </tbody>
            </table>
          )}
        </Card>
      </div>

      <ConfirmDialog
        open={switchingOff !== null}
        title={`Switch '${switchingOff?.name ?? ''}' off in ${countryName}?`}
        body={
          `The value leaves the pickers in ${countryName} immediately; new records here ` +
          'cannot attach it, and the refusal names this country. Records that already ' +
          'carry it keep it, and every other country keeps its own answer. Switching it ' +
          'back on is one click — the decision is recorded either way.'
        }
        confirmLabel="Switch off"
        busy={put.isPending}
        onConfirm={() => {
          if (switchingOff !== null) {
            put.mutate({ platform_id: switchingOff.id, active: false });
          }
        }}
        onCancel={() => setSwitchingOff(null)}
      />
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────── table row ── */

function AvailabilityRow({
  row,
  busy,
  onSwitchOff,
  onSwitchOn,
}: {
  row: CountryPlatformRow;
  busy: boolean;
  onSwitchOff: () => void;
  onSwitchOn: () => void;
}): React.JSX.Element {
  return (
    <tr className="crf-register-row border-b border-line last:border-b-0">
      <td className={`py-2.5 pr-3 text-body ${row.offered ? 'text-fg' : 'text-fg-secondary'}`}>
        {row.name}
      </td>
      <td className="py-2.5 pr-3 text-body13 text-fg-secondary">
        {appliesToSentence(row.applies_to)}
      </td>
      {/* D-2, said: absence of a decision is its own state, never dressed as a choice. */}
      <td className="py-2.5 pr-3 text-body13 text-fg-secondary">
        {row.explicit
          ? row.active
            ? 'Switched on here'
            : 'Switched off here'
          : 'No decision recorded — offered'}
      </td>
      <td className="py-2.5 pr-3">
        <span className="inline-flex flex-wrap items-center gap-1.5">
          {row.offered ? (
            <span className="inline-flex items-center rounded-pill bg-ok-bg px-2 py-0.5 text-label text-ok-fg">
              Offered
            </span>
          ) : (
            /* Bordered, unfilled — the agent-keys/network-rules AA measurement. */
            <span className="inline-flex items-center rounded-pill border border-fg-muted px-2 py-0.5 text-label text-fg-secondary">
              Not offered
            </span>
          )}
          {row.group_active ? null : (
            /* The group switch beats a country YES; a lit row without this pill would lie. */
            <span className="inline-flex items-center rounded-pill border border-fg-muted px-2 py-0.5 text-label text-fg-secondary">
              Retired group-wide
            </span>
          )}
        </span>
      </td>
      <td className="py-2.5 text-right">
        {row.active ? (
          <Button variant="quiet" disabled={busy} onClick={onSwitchOff}>
            Switch off…
          </Button>
        ) : (
          <Button variant="quiet" disabled={busy} onClick={onSwitchOn}>
            Switch on
          </Button>
        )}
      </td>
    </tr>
  );
}
